import React, { useState } from 'react';
import {
  Container,
  TipDrawer,
  ThemeButton,
  ThemeTable,
} from '../../components/components';
import { message } from 'antd';
import { add_icon, delete_icon, edit_icon } from '../../assets/icons/icons';
import { BulbOutlined } from '@ant-design/icons';
import { uploadImage } from '../../helpers/helpers';
import {
  useCreateTipMutation,
  useUpdateTipMutation,
  useDeleteTipMutation,
  useGetTipsQuery,
} from '../../services/tips';

const Tips = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState({});
  const [image, setImage] = useState('');
  // `loader` above is the drawer's submit state — keep the fetch state separate.
  const { data, refetch, isLoading: fetchingTips } = useGetTipsQuery();

  const [createTip] = useCreateTipMutation();
  const [updateTip] = useUpdateTipMutation();
  const [deleteTip] = useDeleteTipMutation();

  const onClose = () => {
    setFormData({});
    setImage('');
    setOpen(false);
  };

  const onSubmit = async () => {
    if (!formData?.image) {
      message.error('Please select an image');
      return;
    }
    if (mode === 'update' && !formData?._id) {
      message.error('Missing tip id — please reopen and try again.');
      return;
    }
    setLoader(true);
    const url = await uploadImage(formData?.image);
    if (!url) {
      setLoader(false);
      message.error('Failed to upload image. Please try again.');
      return;
    }
    let data = { ...formData, image: url };
    let response;
    if (mode === 'create') {
      response = await createTip({ tip: data });
    } else {
      response = await updateTip({
        id: data?._id,
        body: { tip: data },
      });
    }
    if (!response?.data?.error) {
      setLoader(false);
      message.success(
        `Tip ${mode === 'create' ? 'created' : 'updated'} successfully.`
      );
      onClose();
      refetch();
    } else {
      setLoader(false);
      message.error(response?.data?.msg || 'Please fill form correctly.');
    }
  };

  const onEdit = data => {
    setFormData(data);
    setImage(data?.image);
    setMode('update');
    setOpen(true);
  };

  const onDelete = async id => {
    try {
      const res = await deleteTip({ id: id });
      if (!res?.data?.error) {
        message.success('Tip deleted successfully');
        refetch();
      }
    } catch (err) {
      message.error(err);
    }
  };

  const columns = [
    {
      title: 'No',
      dataIndex: 'no',
      key: 'no',
      render: (data, _, index) => (
        <p className="text-center font-medium">{index + 1}</p>
      ),
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (picture, data) => (
        <div className="flex justify-center">
          <img
            className="object-contain"
            src={picture}
            alt=""
            style={{ height: '45px', width: '80px' }}
          />
        </div>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: text => <p className="text-center font-medium">{text}</p>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: text => <p className="text-center font-medium">{text}</p>,
    },
    {
      title: 'In Action',
      dataIndex: '_id',
      key: '_id',
      width: '10%',
      render: (text, data) => (
        <span className="flex justify-center items-center">
          <span className="mx-2 cursor-pointer" onClick={() => onEdit(data)}>
            <img src={edit_icon} alt="" width={15} />
          </span>
          <span
            className="mx-2 cursor-pointer"
            onClick={() => onDelete(data?._id)}
          >
            <img src={delete_icon} alt="" width={15} />
          </span>
        </span>
      ),
    },
  ];

  return (
    <Container
      selected={'6'}
      header={
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-primary h-9 w-9 rounded-full flex justify-center items-center">
              <BulbOutlined className="text-white" />
            </div>
            <p className="ml-2 font-semibold text-2xl">Tips</p>
          </div>

          <ThemeButton
            onClick={() => setOpen(true)}
            className={'ml-3'}
            content={
              <div className="flex items-center">
                <img src={add_icon} alt="" width={18} height={18} />
                <p className="ml-2">Create Tip</p>
              </div>
            }
          />
        </div>
      }
    >
      <ThemeTable
        columns={columns}
        data={data?.data || []}
        loader={fetchingTips}
        entityName="tips"
        emptyText="No tips published yet. Add one to show it to owners in the app."
      />

      <TipDrawer
        formData={formData}
        image={image}
        setImage={e => setImage(e)}
        setFormData={e => setFormData(e)}
        isLoading={loader}
        onSubmit={onSubmit}
        open={open}
        onClose={onClose}
        btnTxt={mode == 'create' ? 'Add Tip' : 'Update Tip'}
        addIcon
      />
    </Container>
  );
};

export default Tips;
